import {
  CreditConsumeAction,
  CreditExpireAction,
  CreditInsertAction,
  CreditTransaction,
  CreditTransactionAction,
  CreditTransactionSource,
  TransactionContext,
  TransactionSources,
  TransactionTypes,
} from "@custom-types/credit-types";

/** @returns True if the object type is Credit Transaction */
export function isCreditTransaction(data: unknown): data is CreditTransaction {
  if (!data || typeof data !== "object") {
    return false;
  }
  const toCheck: Partial<CreditTransaction> = data;
  return (
    typeof toCheck.id === "string" &&
    typeof toCheck.timestamp === "string" &&
    isCreditTransactionSource(toCheck.source) &&
    isCreditTransactionAction(toCheck.action) &&
    isCreditTransactionContext(toCheck.context) &&
    isCreditTransactionResult(toCheck.result) &&
    Array.isArray(toCheck.tags) &&
    (toCheck.visibility === "External" || toCheck.visibility === "Internal")
  );
}

/** @returns True if the object type is Credit Transaction Source */
export function isCreditTransactionSource(
  source: unknown
): source is { type: TransactionSources; version: string } {
  if (!source || typeof source !== "object") {
    return false;
  }
  const s: Partial<CreditTransactionSource> = source;
  return (
    typeof s.type === "string" &&
    Object.values(TransactionSources).includes(s.type) &&
    typeof s.version === "string"
  );
}

/** @returns True if the object type is Credit Transaction Action */
export function isCreditTransactionAction(
  action: unknown
): action is CreditTransaction["action"] {
  if (!action || typeof action !== "object") {
    return false;
  }
  const a = action as Partial<CreditTransactionAction>;
  if (
    typeof a.type !== "string" ||
    !Object.values(TransactionTypes).includes(a.type)
  ) {
    return false;
  }
  switch (a.type) {
    case TransactionTypes.insertTokens:
      return isInsertTokensAction(a);
    case TransactionTypes.consumeTokens:
      return isConsumeTokensAction(a);
    case TransactionTypes.expireTokens:
      return isExpireTokensAction(a);
    default:
      return false;
  }
}

/** @returns True if the action data is an insert action */
export function isInsertTokensAction(
  action: unknown
): action is CreditInsertAction {
  if (!action || typeof action !== "object") {
    return false;
  }
  const a = action as Partial<CreditInsertAction>;
  return (
    a.type === TransactionTypes.insertTokens &&
    typeof a.data?.tokenAmount === "number" &&
    typeof a.data?.reason === "string" &&
    typeof a.data?.trigger === "string"
  );
}

/** @returns True if the action data is a consumption action */
export function isConsumeTokensAction(
  action: unknown
): action is CreditConsumeAction {
  if (!action || typeof action !== "object") {
    return false;
  }
  const a = action as Partial<CreditConsumeAction>;
  return (
    a.type === TransactionTypes.consumeTokens &&
    typeof a.data?.tokenAmount === "number" &&
    typeof a.data?.consumptionReason === "string"
  );
}

/** @returns True if the action data is a expire action */
export function isExpireTokensAction(
  action: unknown
): action is CreditExpireAction {
  if (!action || typeof action !== "object") {
    return false;
  }
  const a = action as Partial<CreditExpireAction>;
  return (
    a.type === TransactionTypes.expireTokens &&
    typeof a.data?.expirationReason === "string"
  );
}

/** @returns True if the object type is Credit Transaction Context */
export function isCreditTransactionContext(
  context: unknown
): context is CreditTransaction["context"] {
  if (!context || typeof context !== "object") {
    return false;
  }
  const c: Partial<TransactionContext> = context;
  return typeof c.companyId === "string";
}

/** @returns True if the object type is Credit Transaction Result */
export function isCreditTransactionResult(
  result: unknown
): result is CreditTransaction["result"] {
  if (!result || typeof result !== "object") {
    return false;
  }

  const r: Partial<Record<string, unknown>> = result;
  const data =
    r.data && typeof r.data === "object" && !Array.isArray(r.data)
      ? (r.data as Record<string, unknown>)
      : undefined;

  switch (r.type) {
    case "TokensInserted":
      return (
        data !== undefined &&
        (typeof data.subscriptionId === "string" ||
          data.subscriptionId === null)
      );

    case "TokensConsumed":
      return data !== undefined && Array.isArray(data.subscriptionIds);

    case "TokenInsertionFailed":
    case "TokenConsumptionFailed":
      return data !== undefined && typeof data.failureReason === "string";

    case "TokenInsertionRejected":
    case "TokenConsumptionRejected":
      return data !== undefined && typeof data.rejectionReason === "string";

    case "TokensExpired":
      return (
        data !== undefined &&
        (typeof data.subscriptionId === "string" ||
          data.subscriptionId === null) &&
        typeof data.expiredAmount === "number"
      );
    default:
      return false;
  }
}
